import React, { useMemo } from 'react';
import {
  TableBody,
  TableRow,
  TableCell,
  Checkbox,
  Skeleton,
  Stack,
  Box,
  Typography,
  Tooltip,
} from '@mui/material';
import { AppIconButton } from '../../design-system';
import { useTheme } from '@mui/material/styles';
import type { TableColumn, RowData, TableAction, EditingCell, TableGroupHeaderContext } from './types';
import EditableCell from './EditableCell';
import EmptyState from '../EmptyState';
import { getTableSelectionColors } from '../../../theme/uiSemanticColors';
import { gentleTableRowBorder } from './tableDividerStyles';

interface EnhancedTableBodyProps<T = RowData> {
  data: T[];
  columns: TableColumn<T>[];
  getRowId: (row: T) => string | number;
  selectable: boolean;
  selectionMode?: 'single' | 'multiple';
  selectedIds: (string | number)[];
  onSelectRow: (rowId: string | number, checked: boolean) => void;
  onRowClick?: (row: T) => void;
  onRowContextMenu?: (event: React.MouseEvent, row: T) => void;
  activeRowId?: string | number;
  rowHeight: number;
  cellPadding?: string;
  actions: TableAction<T>[];
  editingCell: EditingCell | null;
  onCellEdit: (rowId: string | number, field: string, value: unknown) => void;
  onCellSave: (rowId: string | number, field: string, newValue: unknown) => Promise<void>;
  onCellCancel: () => void;
  loading: boolean;
  emptyStateTitle: string;
  emptyStateDescription?: string;
  emptyStateAction?: { label: string; onClick: () => void };
  groupBy?: (row: T) => string;
  getGroupLabel?: (groupKey: string) => string;
  groupDisplayMode?: 'section-header' | 'column';
  renderGroupHeader?: (context: TableGroupHeaderContext) => React.ReactNode;
}

function EnhancedTableBody<T extends object = RowData>(props: EnhancedTableBodyProps<T>) {
  const theme = useTheme();
  const tableSelection = useMemo(() => getTableSelectionColors(theme), [theme]);
  const {
    data,
    columns,
    getRowId,
    selectable,
    selectionMode = 'multiple',
    selectedIds,
    onSelectRow,
    onRowClick,
    onRowContextMenu,
    activeRowId,
    rowHeight,
    cellPadding = '16px 8px',
    actions,
    editingCell,
    onCellEdit,
    onCellSave,
    onCellCancel,
    loading,
    emptyStateTitle,
    emptyStateDescription,
    emptyStateAction,
    groupBy,
    getGroupLabel,
    groupDisplayMode = 'section-header',
    renderGroupHeader,
  } = props;

  const showSelectionColumn = selectable && selectionMode === 'multiple';
  const rowIsInteractive = Boolean(onRowClick) || (selectable && selectionMode === 'single');
  const useGroupColumn = Boolean(groupBy) && groupDisplayMode === 'column';
  const useSectionHeaders = Boolean(groupBy) && groupDisplayMode === 'section-header';
  const dataColSpan =
    columns.length + (showSelectionColumn ? 1 : 0) + (actions.length > 0 ? 1 : 0);

  const groupSpanByRowIndex = useMemo(() => {
    if (!groupBy) return null;
    const spans = new Map<number, { groupKey: string; rowSpan: number }>();
    let index = 0;
    while (index < data.length) {
      const groupKey = groupBy(data[index]);
      let end = index + 1;
      while (end < data.length && groupBy(data[end]) === groupKey) {
        end += 1;
      }
      spans.set(index, { groupKey, rowSpan: end - index });
      index = end;
    }
    return spans;
  }, [data, groupBy]);

  // Show skeleton rows while loading
  if (loading && data.length === 0) {
    return (
      <TableBody>
        {Array.from({ length: 10 }).map((_, index) => (
          <TableRow key={index} sx={{ height: rowHeight }}>
            {showSelectionColumn && (
              <TableCell padding="checkbox">
                <Skeleton variant="rectangular" width={24} height={24} />
              </TableCell>
            )}
            {useGroupColumn && (
              <TableCell>
                <Skeleton variant="text" />
              </TableCell>
            )}
            {columns.map((_, colIndex) => (
              <TableCell key={colIndex}>
                <Skeleton variant="text" />
              </TableCell>
            ))}
            {actions.length > 0 && (
              <TableCell>
                <Skeleton variant="text" />
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    );
  }

  if (data.length === 0) {
    const colSpan = dataColSpan + (useGroupColumn ? 1 : 0);

    return (
      <TableBody>
        <TableRow>
          <TableCell colSpan={colSpan} sx={{ px: 0, py: 2 }}>
            <EmptyState
              title={emptyStateTitle}
              description={emptyStateDescription}
              action={emptyStateAction}
              compact={true}
            />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  }

  const activeColor = tableSelection.selectedRowBg;

  const defaultGroupHeader = (context: TableGroupHeaderContext) => (
    <Stack direction="row" alignItems="center" spacing={1} minWidth={0}>
      <Typography variant="subtitle2" fontWeight={600} noWrap>
        {context.label}
      </Typography>
      <Typography variant="caption" color="text.secondary" noWrap>
        · {context.count} {context.count === 1 ? 'item' : 'items'}
      </Typography>
    </Stack>
  );

  const renderSectionHeaderRow = (
    groupKey: string,
    count: number,
  ) => {
    const label = getGroupLabel?.(groupKey) ?? groupKey;
    const headerContent = (renderGroupHeader ?? defaultGroupHeader)({
      groupKey,
      label,
      count,
    });

    return (
      <TableRow key={`group-${groupKey}`} className="EnhancedTable-groupHeader">
        <TableCell
          colSpan={dataColSpan}
          sx={(theme) => ({
            py: 1,
            padding: cellPadding,
            paddingLeft: '5px',
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            bgcolor: 'action.hover',
            borderTop: 'none',
            borderBottom: gentleTableRowBorder(theme),
          })}
        >
          {headerContent}
        </TableCell>
      </TableRow>
    );
  };

  const renderDataRow = (row: T, rowIndex: number) => {
    const rowId = getRowId(row);
    const isSelected = selectedIds.includes(rowId);
    const isActive = activeRowId !== undefined && rowId === activeRowId;
    const showRowAccent =
      isActive || (selectable && selectionMode === 'single' && isSelected);

    const activeCellStyle = showRowAccent
      ? { backgroundColor: activeColor }
      : undefined;

    const groupSpan = groupSpanByRowIndex?.get(rowIndex);
    const isLastRowBeforeGroupHeader =
      useSectionHeaders
      && rowIndex + 1 < data.length
      && Boolean(groupSpanByRowIndex?.has(rowIndex + 1));

    return (
      <TableRow
        key={String(rowId)}
        hover
        selected={isSelected}
        onClick={() => onRowClick?.(row)}
        onContextMenu={(e) => onRowContextMenu?.(e, row)}
        sx={{
          height: rowHeight,
          cursor: rowIsInteractive ? 'pointer' : 'default',
          ...(isLastRowBeforeGroupHeader
            ? { '& .MuiTableCell-root': { borderBottom: 'none' } }
            : {}),
        }}
      >
        {showSelectionColumn && (
          <TableCell
            padding="checkbox"
            onClick={(e) => e.stopPropagation()}
            style={{
              ...activeCellStyle,
              ...(showRowAccent ? { borderLeft: `3px solid ${theme.palette.primary.main}` } : {}),
            }}
          >
            <Checkbox
              checked={isSelected}
              onChange={(e) => onSelectRow(rowId, e.target.checked)}
            />
          </TableCell>
        )}

        {useGroupColumn && groupSpan && (
          <TableCell
            rowSpan={groupSpan.rowSpan}
            sx={{
              verticalAlign: 'top',
              minWidth: 140,
              width: 140,
              padding: cellPadding,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Typography variant="body2" fontWeight={600}>
              {getGroupLabel?.(groupSpan.groupKey) ?? groupSpan.groupKey}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
              {groupSpan.rowSpan} {groupSpan.rowSpan === 1 ? 'item' : 'items'}
            </Typography>
          </TableCell>
        )}

        {columns.map((column, colIndex) => {
          const field = String(column.field);
          const value = column.valueGetter
            ? column.valueGetter(row)
            : row[column.field as keyof T];

          const isEditing =
            editingCell?.rowId === rowId && editingCell?.field === field;

          const isEditable =
            typeof column.editable === 'function'
              ? column.editable(row)
              : column.editable === true;

          const isFirstCell = colIndex === 0;

          return (
            <TableCell
              key={field}
              onClick={(e) => {
                if (isEditable && !isEditing) {
                  e.stopPropagation();
                  onCellEdit(rowId, field, value);
                }
              }}
              style={{
                ...activeCellStyle,
                ...(showRowAccent && isFirstCell && !showSelectionColumn
                  ? { borderLeft: `3px solid ${theme.palette.primary.main}` }
                  : {}),
              }}
              sx={{
                width: column.width,
                minWidth: column.minWidth,
                maxWidth: column.maxWidth,
                flex: column.flex,
                cursor: isEditable ? 'pointer' : 'default',
                position: 'relative',
                padding: cellPadding,
                ...(isEditable && !isEditing && {
                  '&:hover': {
                    backgroundColor: 'action.hover',
                    '&::after': {
                      content: '"✎"',
                      position: 'absolute',
                      right: 4,
                      top: '50%',
                      transform: 'translateY(-50%)',
                      fontSize: '12px',
                      opacity: 0.5,
                      pointerEvents: 'none',
                    },
                  },
                }),
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'left' ? 'flex-start' : 'center', alignItems: 'center', width: '100%' }}>
                {isEditing ? (
                  <EditableCell
                    row={row}
                    column={column}
                    value={editingCell.value}
                    onSave={(newValue) => onCellSave(rowId, field, newValue)}
                    onCancel={onCellCancel}
                  />
                ) : column.renderCell ? (
                  column.renderCell(row, value)
                ) : column.valueFormatter ? (
                  column.valueFormatter(value)
                ) : (
                  String(value ?? '')
                )}
              </Box>
            </TableCell>
          );
        })}

        {actions.length > 0 && (
          <TableCell onClick={(e) => e.stopPropagation()} style={activeCellStyle} sx={{ padding: cellPadding }}>
            <Stack direction="row" spacing={0.5} justifyContent="center">
              {actions.map((action, index) => {
                const isVisible =
                  action.visible === undefined || action.visible(row);
                const isDisabled =
                  action.disabled !== undefined && action.disabled(row);

                if (!isVisible) return null;

                const tooltipTitle =
                  typeof action.description === 'function'
                    ? action.description(row)
                    : action.description ?? action.label;

                return (
                  <Tooltip key={index} title={tooltipTitle} arrow>
                    <span>
                      <AppIconButton
                        size="small"
                        onClick={() => action.onClick(row)}
                        disabled={isDisabled}
                        color={action.color}
                        aria-label={action.label}
                      >
                        {action.icon}
                      </AppIconButton>
                    </span>
                  </Tooltip>
                );
              })}
            </Stack>
          </TableCell>
        )}
      </TableRow>
    );
  };

  return (
    <TableBody>
      {data.map((row, rowIndex) => {
        const fragments: React.ReactNode[] = [];
        if (useSectionHeaders && groupSpanByRowIndex) {
          const groupSpan = groupSpanByRowIndex.get(rowIndex);
          if (groupSpan) {
            fragments.push(
              renderSectionHeaderRow(
                groupSpan.groupKey,
                groupSpan.rowSpan,
              ),
            );
          }
        }
        fragments.push(renderDataRow(row, rowIndex));
        return (
          <React.Fragment key={String(getRowId(row))}>
            {fragments}
          </React.Fragment>
        );
      })}
    </TableBody>
  );
}

export default EnhancedTableBody;
